import { WebMercatorViewport } from '@deck.gl/core';
import { BASEMAP_ESRI_SATELLITE } from '@rsos/constants/basemapAddonNames';
import { ESRI, GOOGLE } from '@rsos/constants/mapProviders';

export const getGmapsViewState = mapInstance => {
  if (!window.google?.maps) {
    throw new Error(
      'Google Maps JavaScript API is not loaded. Cannot get maps view state and normalize zoom level.',
    );
  }
  const center = mapInstance.getCenter();
  const latLng = new window.google.maps.LatLng(center.lat(), center.lng());
  const lat = latLng.lat();
  const lng = latLng.lng();
  const width = mapInstance.getDiv().offsetWidth;
  const height = mapInstance.getDiv().offsetHeight;
  const viewState = {
    longitude: lng,
    latitude: lat,
    width,
    height,
    zoom: normalizeZoomLevel(mapInstance, GOOGLE),
  };

  return viewState;
};

/**
 * Normalize the zoom level for getViewState for deck.gl to properly handle
 * converting coordinates to pixels on the screen with deck.gl's `viewport`.
 * See more info here https://medium.com/vis-gl/using-deck-gl-with-google-maps-9c868d18e3cd
 * @param {Instance} mapInstance
 * @param {String} mapProvider - ESRI or GOOGLE
 */
export function normalizeZoomLevel(mapInstance, mapProvider) {
  if (mapProvider === ESRI) {
    return mapInstance.view.zoom;
  } else if (mapProvider === GOOGLE) {
    return mapInstance.getZoom() - 1;
  }
}

export const convertStringToNumber = value => {
  if (value === null || value === undefined) {
    return value;
  }

  return Number(value);
};

/**
 * Ensure values align for locations, especially when APIs are expecting numbers
 */
export const normalizeLocation = location => {
  if (!location || Object.keys(location).length === 0) {
    return location;
  }

  const latitude = convertStringToNumber(location.latitude);
  const longitude = convertStringToNumber(location.longitude);
  const uncertainty_radius = convertStringToNumber(location.uncertainty_radius);

  return {
    ...location,
    latitude: latitude,
    longitude: longitude,
    uncertainty_radius: uncertainty_radius,
  };
};

/**
 * Creates a WebMercatorViewport with proper adjustments for satellite mode.
 * Handles the zoom normalization and viewport calculations that differ between
 * satellite and navigation basemaps.
 *
 * @param {Object} viewState - The view state object containing longitude, latitude, zoom, width, height
 * @param {String} basemap - Optional basemap type. If not provided, will check for satellite indicators in viewState
 * @returns {WebMercatorViewport} - Properly configured viewport for the current basemap mode
 */
export const createWebMercatorViewport = (viewState, basemap = null) => {
  const isSatelliteBasemap = basemap
    ? basemap === BASEMAP_ESRI_SATELLITE
    : false;

  // Apply satellite mode adjustments
  const adjustedViewState = isSatelliteBasemap
    ? { ...viewState, zoom: viewState.zoom - 1 }
    : viewState;

  return new WebMercatorViewport(adjustedViewState);
};

/**
 * Takes in the boundingBox and current Map. Centers the map on the boundingBox
 */
export const centerMapOnJursidiction = (boundingBox, map) => {
  if (!map || !boundingBox || boundingBox.length === 0) {
    return;
  }

  let bounding;
  if (Array.isArray(boundingBox)) {
    bounding = boundingBox[0];
  }

  try {
    const { longitude: east, latitude: south } = bounding.bottom_right;
    const { longitude: west, latitude: north } = bounding.top_left;
    map.fitBounds({ west, east, north, south });
  } catch {
    // TODO: Seems safe to remove, once TT ADMIN is done small ticket to remove and properly test
    // Suppressed because this can throw when an alert is declined in RSP.
  }
};
